import { Html } from '@react-three/drei'
import { Box3, MathUtils, Ray, Vector3 } from 'three'
import { modelToUI, scaleModelVectorToUI, uiToModel } from '~/components/DrawingCanvas/util/units'
import { useDistanceDimensions } from '~/hooks/useDistanceDimensions'
import { inputStyles } from '~/ui/Field'
import { R3FDimensionInput } from '~/ui/R3FDimensionInput'
import { forwardRef, Fragment, useImperativeHandle } from 'react'
import { Vector3Like } from 'three'
import { LINE_COLOR, ARROW_SIZE } from '~/components/DrawingCanvas/Products/constants'
import { useCardinalArrowConfigs } from '~/components/DrawingCanvas/Products/hooks/useCardinalArrowConfigs'
import { getArrowDimensions } from '~/components/DrawingCanvas/Products/util'
import { scaleUIVectorToModel } from '~/components/DrawingCanvas/util/units'
import { Obstruction } from '~/store/objects/types'

const ARROW_FLOOR_OFFSET = 30
const BOUNDING_BOX_HEIGHT = 5
const Z_AXIS = new Vector3(0, 0, 1)

export type ArrowHandle = {
  setOrigin: (newOriginInUIUnits: Vector3, dimensions: { length: number; width: number; rotation: number }) => void
}

export const DimensionGuides = forwardRef<
  ArrowHandle,
  {
    isDragging: boolean
    dimensions: { length: number; width: number }
    onUpdatePosition?: (newPosition: Vector3Like) => void
  } & Pick<Obstruction, 'id' | 'position' | 'rotation'>
>(({ id, isDragging, position, onUpdatePosition, dimensions, rotation }, ref) => {
  const arrowConfigs = useCardinalArrowConfigs()
  const { displayFormattedValue } = useDistanceDimensions()

  const arrowOrigin = new Vector3()
  const ray = new Ray()
  const offsetDirection = new Vector3()
  const boundingBox = new Box3()
  const boundingBoxSize = new Vector3()
  const offset = new Vector3()

  const ignoring = id ? new Set([id]) : undefined
  const rotationZ = MathUtils.degToRad(rotation.z)
  const { length, width } = dimensions

  const handleCommit =
    ({ distance, direction }: { distance: number; direction: Vector3 }) =>
    (newValue: number) => {
      const distanceDelta = distance - newValue
      const scaledDirection = new Vector3().copy(direction).multiplyScalar(distanceDelta)
      const newPosition = new Vector3().copy(position).add(scaledDirection)
      onUpdatePosition?.({ x: newPosition.x, y: newPosition.y, z: newPosition.z })
    }

  useImperativeHandle(
    ref,
    () => ({
      setOrigin(newOriginInUIUnits, { length, rotation, width }) {
        arrowConfigs.forEach(({ arrowHelper, direction, htmlMesh, div }) => {
          scaleUIVectorToModel(arrowOrigin.copy(newOriginInUIUnits))
          boundingBox.setFromCenterAndSize(
            arrowOrigin,
            boundingBoxSize.copy({ x: uiToModel(width), y: uiToModel(length), z: BOUNDING_BOX_HEIGHT })
          )
          offsetDirection.copy(direction).applyAxisAngle(Z_AXIS, rotation).normalize()
          ray.set(arrowOrigin, offsetDirection).intersectBox(boundingBox, offset)?.sub(arrowOrigin)
          arrowOrigin.addScaledVector(direction, offset.length())
          const { length: distance } = getArrowDimensions({ position: arrowOrigin, direction, ignoring })
          if (!distance) {
            arrowHelper.current?.setLength(0, 0, 0)
            if (div.current) div.current.style.opacity = '0'
            return
          }
          const arrowLength = modelToUI(distance)
          const zOffset = modelToUI(-(arrowOrigin.z - ARROW_FLOOR_OFFSET))
          arrowHelper.current?.position
            .copy(newOriginInUIUnits)
            .addScaledVector(direction, scaleModelVectorToUI(offset).length())
            .setZ(zOffset)
          arrowHelper.current?.setLength(arrowLength, ARROW_SIZE, ARROW_SIZE)
          htmlMesh.current?.position
            .copy(direction)
            .multiplyScalar((arrowLength + offset.length()) / 2)
            .add(newOriginInUIUnits)
            .setZ(zOffset)
          if (div.current) {
            div.current.textContent = displayFormattedValue(distance)
            div.current.style.opacity = '1'
          }
        })
      },
    }),
    []
  )

  return (
    <>
      {arrowConfigs.map(({ arrowHelper, div, htmlMesh, direction }, i) => {
        const origin = new Vector3().copy(position)
        boundingBox.setFromCenterAndSize(origin, boundingBoxSize.set(width, length, BOUNDING_BOX_HEIGHT))
        const rayDirection = direction.clone().applyAxisAngle(Z_AXIS, rotationZ).normalize()
        ray.set(origin, rayDirection).intersectBox(boundingBox, offset)?.sub(origin)
        origin.addScaledVector(direction, offset.length())
        const { length: distance } = getArrowDimensions({ position: origin, direction, ignoring })
        if (!distance) return null
        return (
          <Fragment key={i}>
            <mesh ref={htmlMesh}>
              {isDragging ? (
                <Html center ref={div} className={inputStyles({ isOnCanvas: true, class: 'pointer-events-none' })}>
                  {displayFormattedValue(distance)}
                </Html>
              ) : (
                <R3FDimensionInput
                  center
                  aria-label={`Product distance guideline ${i}`}
                  value={distance}
                  onCommit={handleCommit({ distance, direction })}
                />
              )}
            </mesh>
            <arrowHelper ref={arrowHelper} args={[direction, undefined, 0, LINE_COLOR, ARROW_SIZE, ARROW_SIZE]} />
          </Fragment>
        )
      })}
    </>
  )
})
